import exceljs from 'exceljs'
import { LABEL_PREFIX, roundHours } from '@/helpers/weekSchedule'

// The printable weekly schedule. French plants have to hand their employees the
// month's shifts at the start of every month, so this sheet is laid out to be
// printed as-is: one row per shift and activity, one column per day, the
// employees assigned to it in the cell. Absences are gathered in a block of
// their own at the bottom.

const HEADER_ROW = 4
const HEADER_FILL = 'ffd7eaf5'
const ACTIVITY_FILL = 'fff7f7f7'
const WEEKEND_FILL = 'fff0f0f0'
const TOTALS_FILL = 'ffebebeb'
// The shades the page itself gives the shifts, so the sheet reads the same way.
const SHIFT_FILLS = { AM: 'ffbee6fa', PM: 'ff96bed2', Night: 'ff6e96aa' }
const ABSENCE_FILL = 'fff6e0e0'

const SHIFT_WIDTH = 8
const ACTIVITY_WIDTH = 18
const DAY_WIDTH = 22
const TOTAL_WIDTH = 10
const CELL_FONT_SIZE = 9
// Column widths are in characters of the default 11pt font, so a 9pt cell fits
// about a fifth more before wrapping. Used to guess how tall a row has to be.
const CHARS_PER_LINE = Math.floor(DAY_WIDTH * 1.2)
const LINE_HEIGHT = 12
const MIN_ROW_HEIGHT = 20

function labelOf (item, t) {
  return item.labelKey ? t(item.labelKey) : item.label
}

function personLine (person, absence, t) {
  return absence ? person.name : person.name + ' · ' + person.hours + ' ' + t(LABEL_PREFIX + '.week.hours_short')
}

function lineCount (text) {
  return Math.max(1, Math.ceil(text.length / CHARS_PER_LINE))
}

// `t` is the caller's own translator called with full message keys, so the sheet
// comes out in the language the manager is reading the page in.
export function buildWeekWorkbook ({ dates, groups, plant, week, locale, t }) {
  const workbook = new exceljs.Workbook()
  workbook.creator = 'GPS Hub'
  const sheet = workbook.addWorksheet(t(LABEL_PREFIX + '.week.label', { n: week }))
  const firstDayColumn = 3
  const lastColumn = dates.length + firstDayColumn
  const border = { style: 'thin', color: { argb: 'ff999999' } }
  const boxed = { top: border, left: border, bottom: border, right: border }
  const centered = { vertical: 'middle', horizontal: 'center', wrapText: true }
  const isWeekend = (column) => {
    const date = dates[column - firstDayColumn]
    return Boolean(date) && (date.getDay() === 0 || date.getDay() === 6)
  }

  sheet.pageSetup = {
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    // A plant with enough staff spills onto a second page, which needs the day
    // names again to be readable.
    printTitlesRow: '1:' + HEADER_ROW,
    margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 }
  }

  sheet.mergeCells(1, 1, 1, lastColumn)
  sheet.getCell('A1').value = plant.name + ' (' + plant.id + ')'
  sheet.getCell('A1').font = { bold: true, size: 14 }
  sheet.mergeCells(2, 1, 2, lastColumn)
  sheet.getCell('A2').value = t(LABEL_PREFIX + '.week.label', { n: week }) + ' · ' + t(LABEL_PREFIX + '.week.range', {
    start: dates[0].toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' }),
    end: dates[dates.length - 1].toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
  })
  sheet.getCell('A2').font = { size: 11, color: { argb: 'ff555555' } }

  const header = sheet.getRow(HEADER_ROW)
  header.height = 30
  header.getCell(1).value = t(LABEL_PREFIX + '.week.shift')
  header.getCell(2).value = t(LABEL_PREFIX + '.week.activity')
  dates.forEach((date, day) => {
    header.getCell(day + firstDayColumn).value = date.toLocaleDateString(locale, { weekday: 'long' }) + '\n' +
      date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' })
  })
  header.getCell(lastColumn).value = t(LABEL_PREFIX + '.week.total')
  header.eachCell({ includeEmpty: true }, (cell) => {
    cell.font = { bold: true }
    cell.alignment = centered
    cell.border = boxed
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } }
  })

  let index = HEADER_ROW
  groups.filter((group) => group.rows.length > 0).forEach((group) => {
    const groupStart = index + 1
    group.rows.forEach((row) => {
      index += 1
      const line = sheet.getRow(index)
      let lines = 1
      row.days.forEach((cell, day) => {
        const target = line.getCell(day + firstDayColumn)
        if (cell.people.length > 0) {
          const text = cell.people.map((person) => personLine(person, row.absence, t))
          target.value = text.join('\n')
          lines = Math.max(lines, text.reduce((sum, entry) => sum + lineCount(entry), 0))
        }
        target.alignment = { vertical: 'top', horizontal: 'left', wrapText: true }
        target.font = { size: CELL_FONT_SIZE }
      })
      line.height = Math.max(MIN_ROW_HEIGHT, (lines * LINE_HEIGHT) + 6)
      line.getCell(2).value = labelOf(row, t)
      line.getCell(2).alignment = { vertical: 'middle', wrapText: true }
      line.getCell(2).font = { bold: true, size: 10 }
      line.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ACTIVITY_FILL } }
      // Absences carry no hours, so a total of 0 would only be misread.
      if (!row.absence) {
        line.getCell(lastColumn).value = row.total
        line.getCell(lastColumn).font = { bold: true }
      }
      line.getCell(lastColumn).alignment = centered
      // Empty days still need their borders, so the run has to include them.
      line.eachCell({ includeEmpty: true }, (cell, column) => {
        cell.border = boxed
        if (isWeekend(column)) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: WEEKEND_FILL } }
        }
      })
    })

    // The shift owns its activities: one merged label down the side of the block.
    if (index > groupStart) {
      sheet.mergeCells(groupStart, 1, index, 1)
    }
    const label = sheet.getCell(groupStart, 1)
    label.value = labelOf(group, t)
    label.font = { bold: true, size: 11 }
    label.alignment = centered
    label.border = boxed
    label.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: group.absence ? ABSENCE_FILL : (SHIFT_FILLS[group.key] || ACTIVITY_FILL) }
    }
  })

  const totals = sheet.getRow(index + 1)
  sheet.mergeCells(index + 1, 1, index + 1, 2)
  totals.getCell(1).value = t(LABEL_PREFIX + '.week.day_total')
  const dayHours = dates.map((date, day) => {
    return roundHours(groups.reduce((sum, group) => {
      return sum + group.rows.reduce((rowSum, row) => rowSum + row.days[day].hours, 0)
    }, 0))
  })
  dayHours.forEach((hours, day) => {
    totals.getCell(day + firstDayColumn).value = hours
  })
  totals.getCell(lastColumn).value = roundHours(dayHours.reduce((sum, hours) => sum + hours, 0))
  totals.eachCell({ includeEmpty: true }, (cell) => {
    cell.font = { bold: true }
    cell.alignment = centered
    cell.border = boxed
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TOTALS_FILL } }
  })

  sheet.getColumn(1).width = SHIFT_WIDTH
  sheet.getColumn(2).width = ACTIVITY_WIDTH
  dates.forEach((date, day) => {
    sheet.getColumn(day + firstDayColumn).width = DAY_WIDTH
  })
  sheet.getColumn(lastColumn).width = TOTAL_WIDTH
  sheet.views = [{ state: 'frozen', xSplit: 2, ySplit: HEADER_ROW }]

  return workbook
}
